import { Link, router, usePage } from '@inertiajs/react'
import { useRef, type MouseEvent, type ReactNode } from 'react'

import { useTheme } from '../hooks/useTheme'
import type { SharedInertiaProps } from '../types/inertiaShared'

type Props = {
  children: ReactNode
}

const NAV = [
  { href: '/about', label: 'About' },
  { href: '/tools/sandbox', label: 'Sandbox' },
  { href: '/tools/sandbox/library', label: 'Library' },
  { href: '/tools', label: 'Tools' },
] as const

function navClass(href: string, currentUrl: string) {
  let active = false
  if (href === '/tools/sandbox/library') {
    active = currentUrl === '/tools/sandbox/library'
  } else if (href === '/tools/sandbox') {
    active = currentUrl === '/tools/sandbox'
  } else if (href === '/about') {
    active = currentUrl === '/about'
  } else if (href === '/tools') {
    const onHub = currentUrl === '/tools'
    const onOtherTool =
      currentUrl.startsWith('/tools/') && !currentUrl.startsWith('/tools/sandbox')
    active = onHub || onOtherTool
  } else {
    active = currentUrl === href || currentUrl.startsWith(`${href}/`)
  }
  return active ? 'nav-link nav-link-active' : 'nav-link'
}

export default function Layout({ children }: Props) {
  const { url, props } = usePage<SharedInertiaProps>()
  const isAdmin = props.isAdmin === true
  const currentUrl = url.split('?')[0] ?? ''
  const onHome = currentUrl === '/' || currentUrl === ''
  const { theme, toggle } = useTheme()
  const nextModeLabel = theme === 'dark' ? 'Light mode' : 'Dark mode'
  const nogrokHomeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function clearNogrokHomeTimer() {
    if (nogrokHomeTimerRef.current != null) {
      clearTimeout(nogrokHomeTimerRef.current)
      nogrokHomeTimerRef.current = null
    }
  }

  function onTinyNogrokClick() {
    clearNogrokHomeTimer()
    nogrokHomeTimerRef.current = setTimeout(() => {
      router.visit('/')
      nogrokHomeTimerRef.current = null
    }, 280)
  }

  function onTinyNogrokDoubleClick(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    clearNogrokHomeTimer()
    router.visit(isAdmin ? '/admin' : '/admin/login')
  }

  return (
    <div className="app-root flex min-h-dvh flex-col">
      <header className="theme-light-chassis-header sticky top-0 z-40 border-b border-outline-variant/20 glass-strip">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 md:px-6">
          <div className="flex items-center gap-8">
            <Link
              href="/"
              className={`font-headline text-xl font-black tracking-tighter uppercase transition-colors ${
                onHome ? 'text-primary-container' : 'text-on-surface hover:text-primary-container/90'
              }`}
            >
              Nogrok
            </Link>
            <nav className="hidden items-center gap-6 md:flex">
              {NAV.map(({ href, label }) => (
                <Link key={href} href={href} className={navClass(href, currentUrl)}>
                  {label.toUpperCase()}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggle}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 font-mono text-[10px] tracking-widest text-on-surface-variant uppercase transition-colors hover:bg-surface-container-low hover:text-primary"
              aria-label={`Switch to ${nextModeLabel}`}
              title={`Switch to ${nextModeLabel}`}
            >
              <span className="material-symbols-outlined text-lg text-primary-container" aria-hidden>
                {theme === 'dark' ? 'light_mode' : 'dark_mode'}
              </span>
              <span className="hidden sm:inline">{theme === 'dark' ? 'Light' : 'Dark'}</span>
            </button>
          </div>
        </div>
        <nav className="theme-light-subnav flex flex-wrap gap-3 border-t border-outline-variant/10 px-4 py-2 md:hidden">
          {NAV.map(({ href, label }) => (
            <Link key={href} href={href} className={navClass(href, currentUrl)}>
              {label.toUpperCase()}
            </Link>
          ))}
        </nav>
      </header>

      <main className="relative z-0 mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 py-6 md:px-8 md:py-8 lg:px-12">
        {/* min-w-0: flex items default to min-width:auto, which lets wide content (tables, charts)
            grow past this container instead of shrinking to it — this is what actually clips them. */}
        <div className="min-w-0 flex-1">{children}</div>
      </main>

      <footer className="theme-light-chassis-footer relative z-0 border-t border-outline-variant/20 bg-surface-container-lowest">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-6 font-mono text-[10px] uppercase tracking-widest text-on-surface-variant md:flex-row">
          <span className="text-primary-container">
            © {new Date().getFullYear()}{' '}
            <button
              type="button"
              onClick={onTinyNogrokClick}
              onDoubleClick={onTinyNogrokDoubleClick}
              className="inline cursor-pointer border-0 bg-transparent p-0 font-mono text-[9px] font-normal lowercase tracking-widest text-primary-container"
              title={
                isAdmin
                  ? 'Home (double-click opens admin dashboard)'
                  : 'Home (double-click opens admin sign-in)'
              }
            >
              nogrok
            </button>
          </span>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <span className="text-on-surface-variant">Local-first tools</span>
            <span>Rails · Inertia · React</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
